// ============================================================================
// THE DURABLE UPLOAD QUEUE — a lecture that survives the phone.
//
// WHAT IT IS FOR. The owner shares a 78-minute recording from Samsung Voice Recorder on the walk
// out of class. Before this file, the bytes lived in a Cache API entry until the page consumed
// them and pushed them at the Worker in one attempt: a tunnel, a locked screen, or Android
// deciding to reclaim the tab lost the lecture, and nothing ever said so. Here the recording is
// written into IndexedDB first, and the send is a retryable state machine that runs whenever the
// app is open.
//
// THE ONE RULE EVERYTHING ELSE FOLLOWS: the local copy is deleted only against a RECEIPT the
// server issued to THIS recording — a read-back whose status is one of queued/claimed/
// transcribing/done AND whose clientUploadId equals this entry's own. Not a status ordering, not
// a PUT's prose, not a 409 that mentions another attempt, and never a job id on its own. A job id
// says a row exists; it does not say whose audio is in it. Everything else keeps the blob.
//
// WHAT THIS IS NOT. It is not background upload. Nothing here runs while the app is closed — no
// Background Fetch, no periodic sync, no promise anywhere in the copy that it will. A reopened
// app SENDS THE WHOLE FILE AGAIN, which is why the UI says "sending again" and never "resuming":
// HTTP range uploads are not part of the intake contract, and a word that implies otherwise is
// the app telling the owner something untrue about his own lecture.
//
// WHY THE STORE IS AN INTERFACE. `QueueStore` below is implemented twice: on IndexedDB, for the
// app, and in memory, for the tests — because happy-dom has no IndexedDB and a queue whose logic
// is only exercised through a browser API that does not exist in the runner is a queue with no
// tests at all. The IndexedDB wiring itself (upgrade, unique index, transaction boundaries) is
// therefore NOT covered by any test in this repo and the PR says so out loud.
//
// The server half is apps/worker/src/routes/audio.ts (PR #98). Its fences — registration keyed on
// clientUploadId, the attempt token on the blob PUT — are what make every retry below safe.
// ============================================================================

import type { ISODate } from '@app/shared'

import {
  RECEIPT_CONFIRMED_STATUSES,
  UploadApiError,
  type UploadBlobInput,
  type UploadBlobResult,
  type UploadRegistration,
  type UploadRegistrationInput,
  type UploadServerStatus,
  type UploadState,
} from './data/types'
import {
  nativeUploaderOwns,
  type NativeRecordingEntry,
  type NativeUploadTokenState,
} from './native-recorder'
import { dropStagedShare, type StagedShare } from './shared-audio'

// ── names and numbers ───────────────────────────────────────────────────────

export const QUEUE_DB_NAME = 'lifeos-upload-queue'
export const QUEUE_DB_VERSION = 1
export const ENTRY_STORE = 'entries'
export const LEASE_STORE = 'lease'
/** Unique. It is what makes re-importing a stage converge instead of duplicating a recording. */
export const STAGE_INDEX = 'by-stage'
export const LEASE_KEY = 'driver'
export const DRIVER_LOCK_NAME = 'lifeos-upload-queue-driver'

/** How long one driver pass may hold the lease before another instance may take it over. */
export const LEASE_MS = 2 * 60 * 1000

/**
 * How long a finished recording stays in the list after its receipt.
 *
 * The blob is gone the moment the receipt arrives; this is metadata only — the owner's proof that
 * the lecture he shared on Tuesday did go through. A list that empties itself the instant an
 * upload succeeds is a list that cannot answer "did it?".
 */
export const DONE_RETENTION_MS = 7 * 24 * 60 * 60 * 1000

/** Exactly what the screen says when the phone would not store the recording. */
export const STORAGE_REFUSED = 'not saved — storage refused'

/** Exactly what the queue says when nobody is signed in. Not an error; a door. */
export const SIGNED_OUT_NOTE = 'signed out — open LifeOS to continue'

// ── the entry ───────────────────────────────────────────────────────────────

/**
 * Where one recording stands: two local words, then the server's own eight.
 *
 * `saved_locally` and `registering` are the only states this app invents, and both describe
 * something that happened HERE. Everything after them is mirrored from a read-back. There is no
 * "uploaded" state of our own, because whether audio arrived is not a thing a client knows.
 */
export type QueueState = 'saved_locally' | 'registering' | UploadServerStatus

export interface QueueEntry {
  /** Primary key. */
  id: string
  /** The Cache API stage this came from. UNIQUE — see `importStage`. */
  stageId: string
  /** Minted once, here, before the first request. Every retry reuses it. */
  clientUploadId: string
  /** Who was signed in when this was saved. An entry is never sent as anybody else. */
  userId: string
  /**
   * The recording. Null ONLY after a receipt for this exact clientUploadId.
   *
   * Null with no receipt on the entry is a bug, and the driver treats it as one: it refuses to
   * send and says so, rather than quietly marking the lecture finished.
   */
  blob: Blob | null
  name: string
  type: string
  size: number
  /**
   * The day the class happened, resolved ONCE when the share was imported.
   *
   * Resolved rather than defaulted at send time, because a recording staged at 23:59 and sent at
   * 00:07 would otherwise state two different days across one retry — and the Worker compares a
   * retry against what the first call actually said (apps/worker/src/routes/audio.ts).
   */
  date: ISODate
  /**
   * The class this recording belongs to, when something KNEW it — never when something guessed.
   *
   * A share off the Android sheet carries no course and leaves this undefined, which registers as
   * `null` exactly as it always did. A native recording carries the course the schedule named
   * (lib/class-labeling.ts), and that is real knowledge about which room the phone was in.
   */
  courseCode?: string | null
  /**
   * What the server is told the recording is called, when something other than the file name
   * KNOWS it. Only the recorder self-test sets it ('Recorder test' — lib/recorder-test.ts);
   * everything else is registered under its literal file name, as it always was.
   */
  title?: string | null
  /** Measured, not derived from the byte count. Undefined when nothing measured it. */
  durationSec?: number | null
  state: QueueState
  jobId?: string
  uploadUrl?: string
  lectureId?: string | null
  /** The last status a read-back reported. `state` follows it; nothing here moves on its own. */
  serverStatus?: UploadServerStatus
  /** Plain words for the row. Null when the last pass had nothing to report. */
  lastError?: string | null

  // ── the receipt, stored because deleting a blob has to be justifiable afterwards ──
  /** The status the read-back reported when the blob was released. */
  receiptStatus?: UploadServerStatus
  receiptAt?: string
  /** The clientUploadId the SERVER named. Kept so the match can be re-checked, not re-guessed. */
  receiptClientUploadId?: string

  /**
   * Set when the server refused this identity (`409 upload_identity_conflict`).
   *
   * The driver stops trying, because nothing about repeating a request the server has already
   * answered will change the answer. The only way forward is a NEW clientUploadId, which is a
   * decision the owner makes, not a retry.
   */
  needsNewIdentity?: boolean
  /** Earliest ms at which to try again, from a `Retry-After`. Honoured, never guessed past. */
  nextAttemptAt?: number
  /** How many passes have tried to move this entry. For the log, never for a decision. */
  attempts: number
  createdAt: string
  updatedAt: string
}

/** One staged share, imported — or refused with the stage left exactly where it was. */
export interface ImportOutcome {
  stageId: string
  entry: QueueEntry | null
  /** False when the entry already existed: this stage had been imported before a crash. */
  created: boolean
  /** Null when the recording is saved. A sentence when it is not. */
  refused: string | null
}

/** The IndexedDB lease, for browsers with no Web Locks. */
export interface QueueLease {
  ownerToken: string
  expiresAt: number
}

// ── the store seam ──────────────────────────────────────────────────────────

export interface QueueStore {
  /**
   * ONE TRANSACTION: write this entry, or find the one that already holds its stage id.
   *
   * The unique index on `stageId` is the whole mechanism. A crash between "the entry committed"
   * and "the staged share was deleted" leaves a stage that is imported a second time on the next
   * open — and this returns the ORIGINAL entry, with its original clientUploadId, rather than a
   * second copy of the same lecture with a second identity.
   */
  importStage(entry: QueueEntry): Promise<{ entry: QueueEntry; created: boolean }>
  all(): Promise<QueueEntry[]>
  get(id: string): Promise<QueueEntry | null>
  put(entry: QueueEntry): Promise<void>
  remove(id: string): Promise<void>
  readLease(): Promise<QueueLease | null>
  /** Compare-and-set. False means somebody else's token was there; take nothing. */
  claimLease(next: QueueLease, expectedToken: string | null): Promise<boolean>
  releaseLease(token: string): Promise<void>
  close(): void
}

// ── IndexedDB ───────────────────────────────────────────────────────────────

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed.'))
  })
}

function finished(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed.'))
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted.'))
  })
}

export function indexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null
  } catch {
    // Some browsers THROW on touching indexedDB in a private window rather than returning null.
    return false
  }
}

/**
 * Open the queue database.
 *
 * Throws when there is no IndexedDB at all (a private window that refuses storage). That throw is
 * not smoothed over anywhere: a queue that silently fell back to memory would promise durability
 * it does not have, and the screen's honest answer is `STORAGE_REFUSED` with the share left
 * staged.
 */
export async function openQueueStore(): Promise<QueueStore> {
  if (!indexedDbAvailable()) {
    throw new Error('This browser has no IndexedDB, so a recording cannot be saved on the phone.')
  }

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open(QUEUE_DB_NAME, QUEUE_DB_VERSION)
    open.onupgradeneeded = () => {
      const database = open.result
      if (!database.objectStoreNames.contains(ENTRY_STORE)) {
        const store = database.createObjectStore(ENTRY_STORE, { keyPath: 'id' })
        store.createIndex(STAGE_INDEX, 'stageId', { unique: true })
      }
      if (!database.objectStoreNames.contains(LEASE_STORE)) {
        database.createObjectStore(LEASE_STORE)
      }
    }
    open.onsuccess = () => resolve(open.result)
    open.onerror = () => reject(open.error ?? new Error('The upload queue database would not open.'))
    open.onblocked = () =>
      reject(new Error('The upload queue database is open in another tab and could not upgrade.'))
  })

  return {
    async importStage(entry) {
      const tx = db.transaction(ENTRY_STORE, 'readwrite')
      const store = tx.objectStore(ENTRY_STORE)
      let outcome: { entry: QueueEntry; created: boolean } | null = null

      const add = store.add(entry)
      add.onsuccess = () => {
        outcome = { entry, created: true }
      }
      add.onerror = (event) => {
        // A DUPLICATE STAGE IS AN ANSWER, NOT A FAILURE. preventDefault stops the constraint
        // error from aborting the transaction, so the lookup below runs inside the SAME
        // transaction that tried to write — there is no window where neither exists.
        event.preventDefault()
        const found = store.index(STAGE_INDEX).get(entry.stageId)
        found.onsuccess = () => {
          const existing = found.result as QueueEntry | undefined
          if (existing) outcome = { entry: existing, created: false }
        }
      }

      await finished(tx)
      if (!outcome) {
        throw new Error('The recording was not written to this phone, and nothing was deleted.')
      }
      return outcome
    },

    async all() {
      const tx = db.transaction(ENTRY_STORE, 'readonly')
      const rows = await request(tx.objectStore(ENTRY_STORE).getAll() as IDBRequest<QueueEntry[]>)
      await finished(tx)
      return rows
    },

    async get(id) {
      const tx = db.transaction(ENTRY_STORE, 'readonly')
      const row = await request(tx.objectStore(ENTRY_STORE).get(id) as IDBRequest<QueueEntry | undefined>)
      await finished(tx)
      return row ?? null
    },

    async put(entry) {
      const tx = db.transaction(ENTRY_STORE, 'readwrite')
      tx.objectStore(ENTRY_STORE).put(entry)
      await finished(tx)
    },

    async remove(id) {
      const tx = db.transaction(ENTRY_STORE, 'readwrite')
      tx.objectStore(ENTRY_STORE).delete(id)
      await finished(tx)
    },

    async readLease() {
      const tx = db.transaction(LEASE_STORE, 'readonly')
      const row = await request(
        tx.objectStore(LEASE_STORE).get(LEASE_KEY) as IDBRequest<QueueLease | undefined>,
      )
      await finished(tx)
      return row ?? null
    },

    async claimLease(next, expectedToken) {
      // READ AND WRITE IN ONE TRANSACTION — that is what makes this a compare-and-set rather than
      // two racing statements. IndexedDB serialises overlapping readwrite transactions on the same
      // store, so the loser sees the winner's token and takes nothing.
      const tx = db.transaction(LEASE_STORE, 'readwrite')
      const store = tx.objectStore(LEASE_STORE)
      const current = await request(store.get(LEASE_KEY) as IDBRequest<QueueLease | undefined>)
      const currentToken = current?.ownerToken ?? null
      if (currentToken !== expectedToken) {
        await finished(tx)
        return false
      }
      store.put(next, LEASE_KEY)
      await finished(tx)
      return true
    },

    async releaseLease(token) {
      const tx = db.transaction(LEASE_STORE, 'readwrite')
      const store = tx.objectStore(LEASE_STORE)
      const current = await request(store.get(LEASE_KEY) as IDBRequest<QueueLease | undefined>)
      // Only ever release OUR OWN lease. Deleting somebody else's is how two drivers end up
      // sending the same file at once — which the server survives, and the data plan does not.
      if (current?.ownerToken === token) store.delete(LEASE_KEY)
      await finished(tx)
    },

    close() {
      db.close()
    },
  }
}

/**
 * The same contract, in memory — what the tests drive.
 *
 * It is NOT a fallback and the app never constructs one: an in-memory queue would claim a
 * recording is saved on the phone when it is saved in a tab. It models the two properties the
 * logic actually depends on — a unique stage id, and a lease that only its owner may move — so a
 * test that passes here is a test about the queue, not about a fake.
 */
export function createMemoryQueueStore(): QueueStore & { rows: Map<string, QueueEntry> } {
  const rows = new Map<string, QueueEntry>()
  let lease: QueueLease | null = null

  const clone = (entry: QueueEntry): QueueEntry => ({ ...entry })

  return {
    rows,
    async importStage(entry) {
      for (const existing of rows.values()) {
        if (existing.stageId === entry.stageId) return { entry: clone(existing), created: false }
      }
      rows.set(entry.id, clone(entry))
      return { entry: clone(entry), created: true }
    },
    async all() {
      return [...rows.values()].map(clone)
    },
    async get(id) {
      const row = rows.get(id)
      return row ? clone(row) : null
    },
    async put(entry) {
      rows.set(entry.id, clone(entry))
    },
    async remove(id) {
      rows.delete(id)
    },
    async readLease() {
      return lease ? { ...lease } : null
    },
    async claimLease(next, expectedToken) {
      if ((lease?.ownerToken ?? null) !== expectedToken) return false
      lease = { ...next }
      return true
    },
    async releaseLease(token) {
      if (lease?.ownerToken === token) lease = null
    },
    close() {
      /* nothing to close */
    },
  }
}

// ── importing staged shares ─────────────────────────────────────────────────

export interface ImportOptions {
  store: QueueStore
  shares: StagedShare[]
  userId: string
  /** The day to file these recordings under, resolved by the caller from its own clock. */
  date: ISODate
  now?: () => number
  newId?: () => string
  /** Deletes the staged share. Injected so the ORDER can be asserted without a Cache API. */
  drop?: (key: string) => Promise<boolean>
}

function uuid(): string {
  // `crypto.randomUUID` is present in every secure context this app runs in. The fallback is not
  // a cryptographic claim — it is "a name unique enough that two shares cannot collide".
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Move every staged share into the queue, then — and only then — delete the stage.
 *
 * THE ORDER IS THE FEATURE. Deleting first, or in the same breath, is how a share is lost to a
 * phone that refused the write. A stage that survives its own import is harmless: the unique
 * index sends the second import back to the first entry.
 */
export async function importStagedShares(options: ImportOptions): Promise<ImportOutcome[]> {
  const { store, shares, userId, date } = options
  const now = options.now ?? (() => Date.now())
  const newId = options.newId ?? uuid
  const drop = options.drop ?? dropStagedShare

  const outcomes: ImportOutcome[] = []
  for (const share of shares) {
    const stamp = new Date(now()).toISOString()
    const entry: QueueEntry = {
      id: newId(),
      stageId: share.stageId,
      clientUploadId: newId(),
      userId,
      blob: share.file,
      name: share.file.name,
      type: share.file.type || 'application/octet-stream',
      size: share.file.size,
      date,
      state: 'saved_locally',
      lastError: null,
      attempts: 0,
      createdAt: stamp,
      updatedAt: stamp,
    }

    let written: { entry: QueueEntry; created: boolean }
    try {
      written = await store.importStage(entry)
    } catch {
      // QUOTA, A CLOSED DATABASE, A BROWSER THAT REFUSES STORAGE. The stage is untouched, so the
      // recording is still in this phone's cache and still in the recorder app, and the screen
      // says the words rather than showing a queue row that never existed.
      outcomes.push({ stageId: share.stageId, entry: null, created: false, refused: STORAGE_REFUSED })
      continue
    }

    // COMMITTED. Now, and not one statement earlier, the staged copy is redundant.
    await drop(share.key).catch(() => false)
    outcomes.push({
      stageId: share.stageId,
      entry: written.entry,
      created: written.created,
      refused: null,
    })
  }
  return outcomes
}

/** The stage id prefix a native recording gets. The PATH is the identity — see below. */
export const NATIVE_STAGE_PREFIX = 'native:'

/**
 * One finished native recording, on its way into the queue.
 *
 * `blob` is nullable and that is the whole shape of today's gap: the recorder plugin answers a
 * filesystem path, and no build of the shell yet lets a WebView read it (lib/native-recorder.ts
 * → `readNativeRecording`). A null blob is REFUSED rather than queued, because an entry with no
 * bytes and no receipt is precisely the impossible state `stepEntry` reports as a bug.
 */
export interface NativeRecordingImport {
  store: QueueStore
  userId: string
  /** The day the class happened, resolved by the caller from the app's clock. */
  date: ISODate
  /** The absolute path the plugin wrote to. Also, verbatim, this entry's stage id. */
  path: string
  /** The class the schedule named, or null when none was near. Never a guess. */
  courseCode: string | null
  /** The recording's bytes, or null when the shell could not hand them over. */
  blob: Blob | null
  /** Measured by the plugin. Null when it reported none. */
  durationSec?: number | null
  /** A title the server should use instead of the file name. Null/absent keeps the file name. */
  title?: string | null
  now?: () => number
  newId?: () => string
}

/** Exactly what the screen says when the app recorded but could not hand the bytes over. */
export const NATIVE_BYTES_UNAVAILABLE =
  'The recording is on this phone, but this build of the LifeOS app cannot hand its bytes to the ' +
  'uploader yet, so nothing has been sent.'

/**
 * Put a recording the native shell just finished into the same queue every share goes through.
 *
 * WHY THE PATH IS THE STAGE ID. `stageId` is unique, and the unique index is what makes a second
 * import converge on the first entry instead of creating a second identity for one lecture. A
 * native recording has exactly one natural unique name — the file it was written to, which the
 * plugin builds from an ISO-8601 timestamp — so a Stop pressed twice, or a screen that reopens
 * mid-import, lands on the recording that is already queued.
 *
 * It shares everything downstream with a shared file: the same `clientUploadId` discipline, the
 * same receipt rule, the same driver. There is deliberately no second upload path.
 */
export async function importNativeRecording(
  options: NativeRecordingImport,
): Promise<ImportOutcome> {
  const { store, userId, date, path, courseCode, blob } = options
  const stageId = `${NATIVE_STAGE_PREFIX}${path}`
  if (!blob) {
    return { stageId, entry: null, created: false, refused: NATIVE_BYTES_UNAVAILABLE }
  }

  const now = options.now ?? (() => Date.now())
  const newId = options.newId ?? uuid
  const stamp = new Date(now()).toISOString()
  const name = path.split(/[\\/]/).pop() || path

  const entry: QueueEntry = {
    id: newId(),
    stageId,
    clientUploadId: newId(),
    userId,
    blob,
    name,
    // The plugin records AAC in MPEG-4 (docs/NATIVE-SPIKE.md). The blob's own type wins when it
    // has one, because the adapter that read the file is closer to the truth than this line.
    type: blob.type || 'audio/mp4',
    size: blob.size,
    date,
    courseCode,
    ...(options.title ? { title: options.title } : {}),
    durationSec: options.durationSec ?? null,
    state: 'saved_locally',
    lastError: null,
    attempts: 0,
    createdAt: stamp,
    updatedAt: stamp,
  }

  try {
    const written = await store.importStage(entry)
    // NOTHING IS DELETED HERE, and that is not an oversight. A shared file's stage is a Cache API
    // handover this app owns; a native recording is a file in the app's own storage that only the
    // native side can remove, and it must not be removed before a receipt anyway.
    return { stageId, entry: written.entry, created: written.created, refused: null }
  } catch {
    return { stageId, entry: null, created: false, refused: STORAGE_REFUSED }
  }
}

/**
 * WHICH FINISHED RECORDINGS ON THE PHONE STILL NEED TO GO INTO THE QUEUE — alarm-started ones.
 *
 * A recording the owner stops from THIS screen is queued by the Stop handler. One a class alarm
 * stopped — with the screen off and the app closed — was never handed to anybody: docs/
 * NATIVE-SPIKE.md → "Hand-off to the upload queue", step 2, says the Recordings screen picks it up
 * the next time it is open, and this is that pick-up. Deliberately narrow:
 *   · ALARM-STARTED only (`scheduled`). A manual recording the owner chose not to send stays his.
 *   · FINISHED only: not being written, container closed (`finalized`), bytes on disk. An
 *     interrupted file cannot be read back as a whole recording and is left where it is.
 *   · Not inside a "Not in class" undo window, and not already handed off (the server has it).
 *   · Not already in the queue under its stage id — importing is idempotent anyway, but reading
 *     40 MB off the phone to learn that is not.
 *   · NOT ONE THE PHONE'S OWN UPLOADER OWNS (`nativeUploaderOwns`, lib/native-recorder.ts — the
 *     one statement of that rule). Importing it here too would be a second job for one lecture.
 */
export function alarmRecordingsToImport(
  recordings: readonly NativeRecordingEntry[],
  entries: readonly Pick<QueueEntry, 'stageId'>[],
  token: NativeUploadTokenState | null = null,
): NativeRecordingEntry[] {
  const queued = new Set(entries.map((entry) => entry.stageId))
  return recordings.filter(
    (recording) =>
      !nativeUploaderOwns(recording, token) &&
      recording.scheduled === true &&
      recording.finalized === true &&
      recording.recording !== true &&
      recording.pendingDiscard !== true &&
      !recording.handedOffAt &&
      !recording.receiptClientUploadId &&
      recording.bytes > 0 &&
      !queued.has(`${NATIVE_STAGE_PREFIX}${recording.path}`),
  )
}

// ── the lock ────────────────────────────────────────────────────────────────

export interface LeaseGuard {
  /** Checked before every write. False means another driver owns the queue now — stop. */
  held(): Promise<boolean>
}

export interface DriverLock {
  /** Runs `fn` while holding the lock. Resolves null when somebody else holds it. */
  run<T>(fn: (guard: LeaseGuard) => Promise<T>): Promise<T | null>
}

interface LockLike {
  request(
    name: string,
    options: { ifAvailable?: boolean; mode?: 'exclusive' | 'shared' },
    fn: (lock: unknown) => Promise<unknown>,
  ): Promise<unknown>
}

function webLocks(): LockLike | null {
  try {
    const locks = (navigator as unknown as { locks?: LockLike }).locks
    return locks && typeof locks.request === 'function' ? locks : null
  } catch {
    return null
  }
}

/**
 * One driver per app instance — not for correctness, for waste.
 *
 * The SERVER is what makes concurrent uploads safe: registration converges on the clientUploadId
 * and the blob PUT is fenced by an attempt token, so two drivers cannot corrupt anything. What
 * they CAN do is send the same 40 MB lecture twice over phone data. Hence a lock, and hence a
 * lock that is allowed to fail: `ifAvailable` gives up immediately rather than queueing a second
 * pass behind the first.
 */
export function webLocksDriverLock(): DriverLock | null {
  const locks = webLocks()
  if (!locks) return null
  return {
    async run<T>(fn: (guard: LeaseGuard) => Promise<T>): Promise<T | null> {
      // The lock is held for the whole callback, so there is nothing to re-check: `held` is the
      // IndexedDB lease's problem, not this one's.
      const guard: LeaseGuard = { held: async () => true }
      const result = await locks.request(DRIVER_LOCK_NAME, { ifAvailable: true }, async (lock) =>
        lock ? await fn(guard) : NOT_HELD,
      )
      return result === NOT_HELD ? null : (result as T)
    },
  }
}

/** A sentinel, so a driver pass that legitimately returns null is not read as "lock refused". */
const NOT_HELD = Symbol('lock-not-held')

/**
 * The fallback for a browser with no Web Locks: an ownership-checked lease in IndexedDB.
 *
 * `held()` re-reads the row before every transition, which is the part that matters. A lease that
 * is only checked when it is taken is a lease that another instance can steal mid-pass while this
 * one keeps writing — and the writes it keeps making would be about a recording it no longer owns.
 */
export function leaseDriverLock(
  store: QueueStore,
  options: { now?: () => number; token?: string; ttlMs?: number } = {},
): DriverLock {
  const now = options.now ?? (() => Date.now())
  const token = options.token ?? uuid()
  const ttlMs = options.ttlMs ?? LEASE_MS

  return {
    async run<T>(fn: (guard: LeaseGuard) => Promise<T>): Promise<T | null> {
      const current = await store.readLease()
      // A live lease belonging to somebody else. Not an error — the other instance is working.
      if (current && current.ownerToken !== token && current.expiresAt > now()) return null

      const expected = current?.ownerToken ?? null
      const claimed = await store.claimLease({ ownerToken: token, expiresAt: now() + ttlMs }, expected)
      if (!claimed) return null

      const guard: LeaseGuard = {
        async held() {
          const lease = await store.readLease()
          return lease?.ownerToken === token && lease.expiresAt > now()
        },
      }
      try {
        return await fn(guard)
      } finally {
        await store.releaseLease(token).catch(() => {})
      }
    },
  }
}

// ── the driver ──────────────────────────────────────────────────────────────

export interface QueueApi {
  registerUpload(input: UploadRegistrationInput): Promise<UploadRegistration>
  putUploadBlob(input: UploadBlobInput): Promise<UploadBlobResult>
  getUploadState(jobId: string): Promise<UploadState>
}

export interface DriverOptions {
  store: QueueStore
  api: QueueApi
  /** The signed-in owner. Null pauses the queue; a DIFFERENT id simply skips those entries. */
  userId: string | null
  now?: () => number
  lock?: DriverLock
  /** Called after every committed change, so a screen can re-read the queue. */
  onChange?: () => void
  log?: (message: string, detail?: unknown) => void
}

export interface QueuePass {
  /** True when the lock was taken and the entries were walked. */
  ran: boolean
  /** Set when nobody is signed in, or the Worker answered 401. Copy for the screen. */
  paused: string | null
  /** True when the pass stopped early because the network is not there. */
  offline: boolean
  /** Entries whose blob was released this pass, against a matching receipt. */
  released: number
  /** Entries removed because their 7 days of metadata were up. */
  expired: number
}

/** Thrown inside a pass to stop it. Never surfaces to a caller. */
class PassHalt extends Error {
  constructor(
    readonly reason: 'signed_out' | 'offline' | 'lost_lease',
    message: string,
  ) {
    super(message)
    this.name = 'PassHalt'
  }
}

function isReceiptStatus(status: UploadServerStatus): boolean {
  return RECEIPT_CONFIRMED_STATUSES.includes(status)
}

/**
 * One pass over the queue.
 *
 * Every entry is moved at most one step; nothing here loops until something succeeds. A pass that
 * kept retrying inside itself would hold the lock through an entire failing network and would be
 * indistinguishable, from the outside, from a hung app.
 */
export async function runQueueOnce(options: DriverOptions): Promise<QueuePass> {
  const { store } = options
  const now = options.now ?? (() => Date.now())
  const log = options.log ?? (() => {})
  const lock = options.lock ?? webLocksDriverLock() ?? leaseDriverLock(store, { now })

  const pass: QueuePass = { ran: false, paused: null, offline: false, released: 0, expired: 0 }

  if (options.userId === null) {
    // NOTHING IS SENT WITHOUT AN OWNER. The entries stay exactly as they are; this is a pause,
    // and the copy says so rather than reporting a failure the owner cannot act on.
    pass.paused = SIGNED_OUT_NOTE
    return pass
  }

  const outcome = await lock.run(async (guard) => {
    pass.ran = true
    const entries = await store.all()

    for (const entry of entries) {
      // An entry that belongs to a different account is never sent, never retried, never touched.
      if (entry.userId !== options.userId) continue
      if (entry.needsNewIdentity) continue
      if (entry.nextAttemptAt !== undefined && entry.nextAttemptAt > now()) continue

      try {
        await stepEntry(entry, { ...options, now, log, guard, pass })
      } catch (error) {
        if (error instanceof PassHalt) {
          if (error.reason === 'signed_out') pass.paused = SIGNED_OUT_NOTE
          if (error.reason === 'offline') pass.offline = true
          if (error.reason === 'lost_lease') log('upload queue: lease lost mid-pass')
          return pass
        }
        // Anything else is this entry's problem and nobody else's: the next entry still gets its
        // turn, and the recording is still on the phone.
        log('upload queue: entry step failed', { id: entry.id, error })
        await save(entry, { lastError: sentence(error) }, { ...options, now, guard })
      }
    }
    return pass
  })

  if (outcome === null) log('upload queue: another driver holds the lock')
  return pass
}

interface StepContext extends DriverOptions {
  now: () => number
  log: (message: string, detail?: unknown) => void
  guard: LeaseGuard
  pass: QueuePass
}

/** A failure in the owner's words. Never a stack trace, never a status code on its own. */
function sentence(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'That did not go through. The recording is still on this phone.'
}

/**
 * Commit one change — and check the lease first, every single time.
 *
 * The check is here rather than at the top of a pass because a lease can expire between two
 * entries. A driver that lost the queue and kept writing would be describing a recording another
 * instance is now sending.
 */
async function save(
  entry: QueueEntry,
  patch: Partial<QueueEntry>,
  ctx: { store: QueueStore; now: () => number; guard: LeaseGuard; onChange?: () => void },
): Promise<QueueEntry> {
  if (!(await ctx.guard.held())) {
    throw new PassHalt('lost_lease', 'Another instance took over the queue.')
  }
  const next: QueueEntry = {
    ...entry,
    ...patch,
    updatedAt: new Date(ctx.now()).toISOString(),
  }
  await ctx.store.put(next)
  ctx.onChange?.()
  Object.assign(entry, next)
  return next
}

/** Turn an API failure into either a pass-level halt or a per-entry patch. */
function halt(error: unknown): void {
  if (error instanceof UploadApiError) {
    if (error.status === 401 || error.code === 'not_signed_in') {
      throw new PassHalt('signed_out', SIGNED_OUT_NOTE)
    }
    if (error.code === 'offline') {
      throw new PassHalt('offline', error.message)
    }
  }
}

async function stepEntry(entry: QueueEntry, ctx: StepContext): Promise<void> {
  // ── retention: a finished recording's metadata, seven days on ─────────────
  if (entry.state === 'done' && entry.receiptAt) {
    const age = ctx.now() - Date.parse(entry.receiptAt)
    if (Number.isFinite(age) && age > DONE_RETENTION_MS) {
      if (!(await ctx.guard.held())) throw new PassHalt('lost_lease', 'Another instance took over.')
      await ctx.store.remove(entry.id)
      ctx.pass.expired += 1
      ctx.onChange?.()
    }
    // Either way there is nothing left to ask about a receipted, finished recording. Polling it
    // for another seven days would be a request per pass that cannot learn anything.
    return
  }

  // `failed` and `cancelled` are the PC's and the owner's last words on this job. The row keeps
  // its blob and offers "send as a new upload"; re-asking a closed job changes nothing.
  if (entry.state === 'failed' || entry.state === 'cancelled') return

  await save(entry, { attempts: entry.attempts + 1 }, ctx)

  // ── register, or re-find, the job ─────────────────────────────────────────
  if (!entry.jobId) {
    await save(entry, { state: 'registering' }, ctx)
    let registered: UploadRegistration
    try {
      registered = await ctx.api.registerUpload({
        clientUploadId: entry.clientUploadId,
        contentType: entry.type,
        filename: entry.name,
        // The literal filename. The owner recognises 'Voice 013.m4a'. The one exception is a
        // title something KNEW (the recorder self-test), which the entry carries explicitly.
        title: entry.title || entry.name,
        // Whatever the entry actually knows. Shares know nothing and still send null.
        courseCode: entry.courseCode ?? null,
        date: entry.date,
        durationSec: entry.durationSec ?? null,
      })
    } catch (error) {
      halt(error)
      if (error instanceof UploadApiError && error.code === 'upload_identity_conflict') {
        // The server has this name on a DIFFERENT recording. Repeating the request cannot change
        // that, so the driver stops and the screen offers the only real remedy: send it as a new
        // upload, with a new name, which is a decision and not a retry.
        await save(entry, { needsNewIdentity: true, lastError: error.message }, ctx)
        return
      }
      if (error instanceof UploadApiError && error.code === 'lecture_already_linked') {
        await save(entry, { needsNewIdentity: true, lastError: error.message }, ctx)
        return
      }
      // EVERYTHING ELSE LEAVES THE ENTRY IN `registering` ON PURPOSE. A lost response is not a
      // reason to mint a new identity — the next pass registers again with the SAME
      // clientUploadId, which the Worker converges onto the job the first call may already have
      // made (apps/worker/src/routes/audio.ts).
      await save(entry, { lastError: sentence(error) }, ctx)
      return
    }

    if (entry.size > registered.maxBytes) {
      await save(
        entry,
        {
          needsNewIdentity: false,
          lastError:
            `That recording is ${Math.round(entry.size / 1024 / 1024)} MB and one upload cannot ` +
            `exceed ${Math.round(registered.maxBytes / 1024 / 1024)} MB. Split it, or drop the ` +
            'file into the desktop worker’s folder, which does not go through this endpoint.',
          nextAttemptAt: Number.MAX_SAFE_INTEGER,
        },
        ctx,
      )
      return
    }

    await save(
      entry,
      {
        jobId: registered.jobId,
        uploadUrl: registered.uploadUrl,
        lectureId: registered.lectureId,
        state: 'awaiting_audio',
        serverStatus: 'awaiting_audio',
        lastError: null,
      },
      ctx,
    )
  }

  // ── ask the server where this actually stands ─────────────────────────────
  const state = await reconcile(entry, ctx)
  if (!state) return

  // ── send, but ONLY against a read-back that says the job wants bytes ──────
  if (state.status !== 'awaiting_audio') return

  if (!entry.blob) {
    // The blob is gone and the job wants audio. That combination should be impossible — a blob is
    // only released against a receipt — so it is reported rather than papered over.
    await save(
      entry,
      {
        lastError:
          'This recording is no longer stored on the phone, but the server is still waiting for ' +
          'it. Share it again from the recorder app.',
        needsNewIdentity: true,
      },
      ctx,
    )
    return
  }

  try {
    await ctx.api.putUploadBlob({
      uploadUrl: entry.uploadUrl!,
      blob: entry.blob,
      contentType: entry.type,
    })
  } catch (error) {
    halt(error)
    if (error instanceof UploadApiError) {
      // `upload_in_progress` is not a failure: another attempt of THIS recording holds the job.
      // Honour the Worker's own Retry-After rather than inventing a backoff.
      if (error.code === 'upload_in_progress') {
        await save(
          entry,
          {
            state: error.serverStatus ?? entry.state,
            serverStatus: error.serverStatus ?? entry.serverStatus,
            lastError: null,
            nextAttemptAt: ctx.now() + (error.retryAfterSec ?? 30) * 1000,
          },
          ctx,
        )
        return
      }
      // `upload_superseded` says these bytes were discarded and NOTHING about whether the audio
      // arrived. The read-back below is the only thing allowed to decide that.
      await save(entry, { lastError: error.message }, ctx)
      await reconcile(entry, ctx)
      return
    }
    throw error
  }

  // The PUT was accepted — and its answer is still not a receipt. Ask.
  await reconcile(entry, ctx)
}

/**
 * Read the job back, mirror it onto the entry, and release the blob if — and only if — this
 * device has a receipt for THIS recording.
 *
 * Returns the state it read, or null when it could not read one.
 */
async function reconcile(entry: QueueEntry, ctx: StepContext): Promise<UploadState | null> {
  let state: UploadState
  try {
    state = await ctx.api.getUploadState(entry.jobId!)
  } catch (error) {
    halt(error)
    if (error instanceof UploadApiError && error.status === 404) {
      // The server has no such job. Registering again with the SAME clientUploadId is the safe
      // move — it converges if the job exists after all, and creates one if it truly does not.
      await save(
        entry,
        { jobId: undefined, uploadUrl: undefined, state: 'registering', lastError: error.message },
        ctx,
      )
      return null
    }
    await save(entry, { lastError: sentence(error) }, ctx)
    return null
  }

  const patch: Partial<QueueEntry> = {
    state: state.status,
    serverStatus: state.status,
    lectureId: state.lectureId ?? entry.lectureId ?? null,
    lastError: state.lastError,
    nextAttemptAt: undefined,
  }

  if (isReceiptStatus(state.status)) {
    if (state.clientUploadId === entry.clientUploadId) {
      // THE RECEIPT, AND THE ONLY PLACE A BLOB IS EVER DROPPED. Stored on the entry, so the
      // deletion can be justified afterwards rather than re-derived.
      patch.receiptStatus = state.status
      patch.receiptAt = new Date(ctx.now()).toISOString()
      patch.receiptClientUploadId = state.clientUploadId
      if (entry.blob) {
        patch.blob = null
        ctx.pass.released += 1
      }
    } else {
      // A JOB WE CAN READ, HOLDING SOMEBODY ELSE'S UPLOAD — or an older one of ours under a
      // different name. Either way this is not our receipt, so the recording stays on the phone.
      ctx.log('upload queue: receipt clientUploadId mismatch — keeping the local copy', {
        entry: entry.id,
        jobId: state.jobId,
        expected: entry.clientUploadId,
        server: state.clientUploadId,
      })
      patch.lastError =
        'The server has this job under a different upload id, so this recording is being kept on ' +
        'the phone. Send it as a new upload if it never arrives.'
    }
  }

  await save(entry, patch, ctx)
  return state
}

// ── the actions a person takes on a row ─────────────────────────────────────

/** Try this one now: clear the wait and let the next pass pick it up. */
export async function retryNow(store: QueueStore, id: string, now = () => Date.now()): Promise<void> {
  const entry = await store.get(id)
  if (!entry) return
  await store.put({
    ...entry,
    nextAttemptAt: undefined,
    lastError: null,
    updatedAt: new Date(now()).toISOString(),
  })
}

/**
 * Send a failed or cancelled recording as a NEW upload.
 *
 * A new clientUploadId is the entire point: the old one names a job the server has closed, and
 * re-registering under it would either collide or resurrect a job the PC gave up on. The stage id
 * is kept, so the crash-boundary rule still holds for this entry.
 */
export async function sendAsNewUpload(
  store: QueueStore,
  id: string,
  options: { now?: () => number; newId?: () => string } = {},
): Promise<void> {
  const now = options.now ?? (() => Date.now())
  const newId = options.newId ?? uuid
  const entry = await store.get(id)
  if (!entry) return
  if (!entry.blob) return
  await store.put({
    ...entry,
    clientUploadId: newId(),
    jobId: undefined,
    uploadUrl: undefined,
    lectureId: null,
    state: 'saved_locally',
    serverStatus: undefined,
    lastError: null,
    receiptStatus: undefined,
    receiptAt: undefined,
    receiptClientUploadId: undefined,
    needsNewIdentity: false,
    nextAttemptAt: undefined,
    attempts: 0,
    updatedAt: new Date(now()).toISOString(),
  })
}

/**
 * Forget a recording. The owner's decision, and it is confirmed on the screen before it gets here.
 *
 * THE SAMSUNG RECORDING IS NEVER TOUCHED. This deletes LifeOS's copy and nothing else; the file
 * in the recorder app is the owner's, and no part of this app has any business with it.
 */
export async function removeEntry(store: QueueStore, id: string): Promise<void> {
  await store.remove(id)
}

/** One pass's body, as the screen's hook builds it. */
export type QueuePassRun = () => Promise<void>

/** What `useUploadQueue` calls instead of running a pass directly. */
export interface PassGate {
  /**
   * Run a pass now — or, when one is already running in this tab, run ONE more the moment it
   * finishes. Any number of requests during a pass fold into that single follow-up, which uses
   * the newest `run` (the hook's closure may have changed while the pass was in flight).
   */
  request: (run: QueuePassRun) => Promise<void>
}

/**
 * One pass at a time inside this tab, and no request dropped on the floor.
 *
 * Before this, a pass asked for while another was mid-flight simply returned. The import that
 * follows Stop asks for exactly such a pass, so a recording made while the mount pass (or the
 * 20 s interval pass) was running sat as "Saved on this phone" until the NEXT trigger — up to
 * 20 s, or until the owner tapped it, which read as "it did not upload until I tapped it" (owner
 * report, 2026-09-24). A request during a pass is now remembered and answered the moment the pass
 * ends. No polling changed and no listener was added: the same three triggers, no longer lost.
 */
export function createPassGate(): PassGate {
  let busy = false
  let pending: QueuePassRun | null = null
  // A function rather than an inline read: TypeScript would narrow `pending` to null after the
  // reset below and not see the write another request makes across the `await`.
  const takePending = (): QueuePassRun | null => {
    const next = pending
    pending = null
    return next
  }
  return {
    async request(run) {
      if (busy) {
        pending = run
        return
      }
      busy = true
      pending = null
      try {
        let next: QueuePassRun | null = run
        while (next) {
          await next()
          next = takePending()
        }
      } finally {
        busy = false
        pending = null
      }
    },
  }
}
